/**
 * features/hr/PayslipsTab.tsx
 * ------------------------------------------------------------------
 * HR PAYROLL — the Payslips tab on a staff profile (Phase 1, Day 6).
 *
 * Generate one staff member's payslip for a chosen month:
 *   - live preview of the reconciled earnings/deductions/net,
 *   - Download PDF (school layout) without saving, and
 *   - Generate & save → renders the PDF, uploads it to Storage
 *     (payslips/{staffId}/{id}.pdf), and writes an immutable
 *     hrPayslips row with the tax-table version stamped.
 * Plus the archive: every saved payslip, newest first, with download.
 *
 * The bulk pay run (Day 7) reuses the exact same assembly + PDF.
 * ------------------------------------------------------------------ */
import { useMemo, useState, useEffect } from 'react';
import {
  Receipt, Download, FileText, Loader2, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import { SoftCard, StatChip, AccentButton, glassFieldClass } from '@/components/ui/glass';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { useAuth } from '@/contexts/AuthContext';
import { hr } from '@/features/hr/hrWrite';
import { useLeavePolicies } from '@/features/hr/useLeavePolicies';
import { recordHrAudit } from '@/lib/hrAudit';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { buildPayslipPdf } from '@/lib/payslipPdf';
import {
  assemblePayslip, payslipId, monthPeriod, periodLabel,
  type AssembledPayslip,
} from '@/lib/assemblePayslip';
import { staffFullName, type HrStaff } from '@/lib/hr';
import {
  computeGratuityTax, fmtPula, MONTH_LABELS,
  contractGratuityBasePaid, gratuityAccruedOn,
  deductionForPeriod, DEDUCTION_TYPES,
  type HrPayrollProfile, type HrDeduction, type HrTaxTable,
  type HrLeaveAllocation, type HrPayslip, type HrPayrollSettings,
} from '@/lib/hrPayroll';
import type { LeaveRequestLike } from '@/lib/hrLeave';

interface ContractLike { startDate: string; endDate?: string; active?: boolean }

export default function PayslipsTab({
  staff, profile, deductions, activeTaxTable, leaveAllocation, leave,
  contracts, payslips, settings, canEdit,
}: {
  staff: HrStaff;
  profile?: HrPayrollProfile;
  deductions: HrDeduction[];
  activeTaxTable?: HrTaxTable;
  leaveAllocation?: HrLeaveAllocation;
  leave: LeaveRequestLike[];
  contracts: ContractLike[];
  payslips: HrPayslip[];
  settings?: HrPayrollSettings;
  canEdit: boolean;
}) {
  const now = new Date();
  const { policies: leavePolicies } = useLeavePolicies();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [isFinal, setIsFinal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>(
    { show: false, status: 'saving', message: '' },
  );

  const { currentUser } = useAuth();
  const actor = { id: currentUser?.uid ?? 'unknown', email: currentUser?.email };

  const period = useMemo(() => monthPeriod(year, month), [year, month]);
  const reference = `SLIP/${year}-${String(month).padStart(2, '0')}/${staff.employeeNo}`;

  // Contract years (for gratuity), from the active contract if datable.
  const contractYears = useMemo(() => {
    const c = contracts.find((x) => x.active) ?? contracts[0];
    if (!c?.startDate) return 2;
    const start = new Date(c.startDate).getTime();
    const end = (c.endDate ? new Date(c.endDate) : new Date()).getTime();
    const yrs = (end - start) / (365.25 * 24 * 3600 * 1000);
    return Math.max(1, Math.round(yrs));
  }, [contracts]);

  const basic = profile?.basicSalary ?? 0;
  const gratuityRate = profile?.gratuityRatePct ?? 0;

  // Final month of the active contract = the selected period lands on the
  // contract's end month. Gratuity is paid out at contract end, so this is
  // the month the payroll preparer must not miss.
  const activeContract = useMemo(() => contracts.find((x) => x.active) ?? contracts[0], [contracts]);
  const isFinalContractMonth = useMemo(() => {
    if (!activeContract?.endDate) return false;
    const e = new Date(activeContract.endDate);
    return e.getFullYear() === year && e.getMonth() + 1 === month;
  }, [activeContract, year, month]);
  const contractEndLabel = activeContract?.endDate
    ? new Date(activeContract.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
  // Auto-surface the gratuity payout UI when preparing the final month.
  useEffect(() => {
    if (isFinalContractMonth && profile?.gratuityEligible) setIsFinal(true);
  }, [isFinalContractMonth, profile?.gratuityEligible]);
  // Gratuity base = total basic paid this contract (opening + payslips
  // since the contract start + the month being prepared). Gratuity =
  // rate × that base. Exact even when basic changed mid-contract.
  const gratuityBasePaid = useMemo(
    () => contractGratuityBasePaid({
      openingBasicPaid: profile?.openingBasicPaidThisContract,
      contractStartISO: activeContract?.startDate,
      savedPayslips: payslips,
      currentPeriod: period,
      currentBasic: basic,
    }),
    [profile?.openingBasicPaidThisContract, activeContract, payslips, period, basic],
  );
  const suggestedGross = gratuityAccruedOn(gratuityBasePaid, gratuityRate);
  const [grossGratuity, setGrossGratuity] = useState(String(suggestedGross));
  // Track the (exact) suggestion when the staff/period/contract changes;
  // manual edits stick until one of those changes again.
  useEffect(() => { setGrossGratuity(String(suggestedGross)); }, [suggestedGross]);

  const gratuity = useMemo(() => {
    if (!isFinal || !profile?.gratuityEligible || !activeTaxTable) return undefined;
    return computeGratuityTax({
      grossGratuity: Number(grossGratuity) || 0,
      contractYears,
      currentAnnualSalary: basic * 12,
      table: activeTaxTable,
    });
  }, [isFinal, profile, activeTaxTable, grossGratuity, contractYears, basic]);

  const assembled: AssembledPayslip = useMemo(
    () => assemblePayslip({
      staff, profile, deductions, taxTable: activeTaxTable,
      leaveAllocation, leaveRequests: leave, period, reference, gratuity,
      // Same leave source as the leave module (policies resolve the cohort
      // entitlement); without them the payslip disagrees with the leave screen.
      leavePolicies, leaveAccrualStart: settings?.leaveAccrualStart,
      sickBalancesFromMonth: settings?.sickBalancesFromMonth,
    }),
    [staff, profile, deductions, activeTaxTable, leaveAllocation, leave, period, reference, gratuity, settings, leavePolicies],
  );

  const id = payslipId(staff.id, period);
  const existing = payslips.find((p) => p.id === id);
  const noProfile = !profile || (profile.basicSalary ?? 0) === 0;

  // A FINALISED payslip IS the official, emailed record. Show its STORED
  // snapshot (not a live recompute) so the on-screen figures always match the
  // PDF the staff member received — even after the active tax table, salary,
  // allowances or deductions change later. A draft/unsaved slip shows the live
  // assemble. Generate & save always uses the live `assembled`.
  const isFinalised = existing?.status === 'finalised';
  const view: AssembledPayslip = isFinalised ? (existing as unknown as AssembledPayslip) : assembled;

  async function download() {
    const logo = await loadLogoDataUrl();
    const pdf = buildPayslipPdf(view, { logoDataUrl: logo, disclaimer: settings?.payslipDisclaimer });
    pdf.save(`payslip_${staff.employeeNo}_${year}-${String(month).padStart(2, '0')}.pdf`);
  }

  async function generateAndSave() {
    setBusy(true);
    setSaveModal({ show: true, status: 'saving', message: 'Rendering & saving payslip…' });
    try {
      const logo = await loadLogoDataUrl();
      const pdf = buildPayslipPdf(assembled, { logoDataUrl: logo, disclaimer: settings?.payslipDisclaimer });
      const blob = pdf.output('blob');
      const path = `payslips/${staff.id}/${id}.pdf`;
      const sref = ref(storage, path);
      await uploadBytes(sref, blob, { contentType: 'application/pdf' });
      const url = await getDownloadURL(sref);
      const nowIso = new Date().toISOString();
      await hr.payslips.set(id, {
        ...assembled,
        basic,
        status: 'finalised',
        pdfUrl: url,
        storagePath: path,
        finalisedAt: nowIso,
        finalisedBy: actor.id,
      });
      await recordHrAudit({
        action: 'payslip.generate',
        actor,
        subjectId: id,
        after: { period: periodLabel(period), net: assembled.net, taxTable: assembled.taxTableId },
      });
      // Auto-archive any finite loan cleared (balance hit zero) by this run.
      for (const d of deductions) {
        if (d.status !== 'active' || !DEDUCTION_TYPES[d.type].balanceKey) continue;
        const res = deductionForPeriod(d, period);
        if (res.remainingAfter !== null && res.remainingAfter <= 0) {
          try { await hr.deductions.update(d.id, { status: 'paid_off' }); } catch { /* non-fatal */ }
        }
      }
      setSaveModal({ show: true, status: 'success', message: `Payslip for ${periodLabel(period)} saved.` });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function reDownload(p: HrPayslip) {
    if (p.pdfUrl) { window.open(p.pdfUrl, '_blank'); return; }
    const logo = await loadLogoDataUrl();
    const pdf = buildPayslipPdf(p, { logoDataUrl: logo, disclaimer: settings?.payslipDisclaimer });
    pdf.save(`payslip_${p.employeeNo}_${p.period.year}-${String(p.period.month).padStart(2, '0')}.pdf`);
  }

  const archive = [...payslips].sort(
    (a, b) => (b.period.year - a.period.year) || (b.period.month - a.period.month),
  );

  return (
    <div className="space-y-4">
      {/* Generate */}
      <SoftCard hover={false}>
        <h3 className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2 mb-4">
          <Receipt size={16} /> Generate payslip
        </h3>

        {noProfile && (
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 text-sm mb-3">
            <AlertTriangle size={15} /> No basic salary set. Add it on the Payroll tab first.
          </div>
        )}
        {!activeTaxTable && (
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 text-sm mb-3">
            <AlertTriangle size={15} /> No active tax table — PAYE will show as 0 until an admin activates one.
          </div>
        )}

        {isFinalContractMonth && (
          <div className="mb-4 flex items-start gap-3 rounded-xl border-2 border-amber-400 bg-amber-50 dark:border-amber-500/60 dark:bg-amber-900/30 px-4 py-3 shadow-sm">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-300 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-bold text-amber-800 dark:text-amber-200">
                Final month of contract — {staffFullName(staff)}&apos;s contract ends {contractEndLabel}.
              </p>
              <p className="text-amber-700 dark:text-amber-300 mt-0.5">
                {profile?.gratuityEligible
                  ? 'This is the gratuity pay-out month — the gratuity calculation has been opened below. Review it carefully before saving, as gratuity is paid at the end of each contract and resets afterwards.'
                  : 'This employee is not gratuity-eligible — confirm final dues, any leave pay-out and statutory severance before saving.'}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Month</span>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={glassFieldClass('py-2')}>
              {MONTH_LABELS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Year</span>
            <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className={glassFieldClass('py-2')} />
          </label>
          {profile?.gratuityEligible && (
            <label className="flex items-end gap-2 pb-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer col-span-2">
              <input type="checkbox" checked={isFinal} onChange={(e) => setIsFinal(e.target.checked)} className="rounded" />
              Final payslip (include gratuity payout)
            </label>
          )}
        </div>

        {isFinal && profile?.gratuityEligible && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40">
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Contract years</span>
              <input value={contractYears} disabled className={glassFieldClass('py-2 opacity-70')} />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Gross gratuity (BWP)</span>
              <input type="number" value={grossGratuity} onChange={(e) => setGrossGratuity(e.target.value)} className={glassFieldClass('py-2')} />
            </label>
            {gratuity && (
              <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-col justify-end pb-1">
                <span>Exempt 50%: {fmtPula(gratuity.exemptAmount)}</span>
                <span>Tax @ {gratuity.marginalRatePct}%: {fmtPula(gratuity.tax)}</span>
              </div>
            )}
          </div>
        )}

        {/* Preview summary — finalised slips show their stored snapshot */}
        {isFinalised && (
          <div className="text-xs text-emerald-700 dark:text-emerald-300 mb-2">
            Showing the finalised payslip{existing?.finalisedAt ? ` (${new Date(existing.finalisedAt).toLocaleDateString()})` : ''} — these are the exact figures on the emailed PDF.
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <StatChip tone="blue" value={fmtPula(view.earningsTotal)} label="Gross" />
          <StatChip tone="gold" value={fmtPula(view.paye)} label="PAYE" />
          <StatChip tone="red" value={fmtPula(view.deductionsTotal)} label="Deductions" />
          <StatChip tone="emerald" value={fmtPula(view.net)} label="Net pay" />
        </div>

        {/* Line preview */}
        <div className="rounded-xl border border-gray-100 dark:border-gray-700 divide-y divide-gray-50 dark:divide-gray-700/50 mb-4 text-sm">
          {view.lines.map((l, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-1.5">
              <span className={l.kind === 'deduction' ? 'text-gray-600 dark:text-gray-300' : 'text-gray-800 dark:text-gray-100'}>
                {l.kind === 'deduction' ? '− ' : ''}{l.label}
                {typeof l.remainingBalance === 'number' && (
                  <span className="text-xs text-gray-400 ml-1">(bal {fmtPula(l.remainingBalance)})</span>
                )}
              </span>
              <span className={l.kind === 'deduction' ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-100 font-medium'}>
                {fmtPula(l.amount)}
              </span>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2">
          {existing && (
            <span className="text-xs text-amber-600 dark:text-amber-300 mr-auto">
              A payslip for {periodLabel(period)} already exists — saving replaces it.
            </span>
          )}
          <button onClick={download} disabled={noProfile} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-50">
            <Download size={15} /> Download PDF
          </button>
          {canEdit && (
            <AccentButton icon={busy ? Loader2 : CheckCircle2} disabled={busy || noProfile} onClick={generateAndSave}>
              {busy ? 'Saving…' : 'Generate & save'}
            </AccentButton>
          )}
        </div>
      </SoftCard>

      {/* Archive */}
      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
          <FileText size={16} className="text-gray-500" />
          <h3 className="font-semibold text-gray-800 dark:text-gray-100">Payslip archive</h3>
        </div>
        {archive.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 px-5 py-4">
            No payslips yet for {staffFullName(staff)}.
          </p>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {archive.map((p) => (
              <div key={p.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-100">{periodLabel(p.period)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Net {fmtPula(p.net)} · {p.reference}
                    {p.gratuity ? ' · incl. gratuity' : ''}
                  </p>
                </div>
                <button onClick={() => reDownload(p)} className="flex items-center gap-1.5 text-xs font-medium text-[var(--accent-link,#2d5295)] hover:underline dark:text-[var(--accent-link-dark,#93c5fd)]">
                  <Download size={13} /> Download
                </button>
              </div>
            ))}
          </div>
        )}
      </SoftCard>

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal((m) => ({ ...m, show: false }))}
      />
    </div>
  );
}
